/**
 * CLI handler for `nexus stop`.
 *
 * Verifies the process on PORT is a Nexus backend (via /api/health)
 * and optionally checks instanceId matches the saved session.
 * --force bypasses all checks and kills by port directly.
 */

import { execSync } from 'node:child_process'
import { readFileSync, existsSync } from 'node:fs'
import { join } from 'node:path'
import { homedir } from 'node:os'
import { consola } from 'consola'
import { getProjectPath } from '@kernel/config/paths.js'

// ── Health check ──────────────────────────────────────────────────────────

interface StopHealthCheck {
  isNexus: boolean
  instanceId: string | null
}

async function checkHealth(port: string): Promise<StopHealthCheck> {
  try {
    const res = await fetch(`http://localhost:${port}/api/health`, {
      signal: AbortSignal.timeout(2000),
    })
    if (!res.ok) return { isNexus: false, instanceId: null }
    const data = await res.json() as { status?: string; instanceId?: string | null }
    return {
      isNexus: data.status === 'ok',
      instanceId: data.instanceId ?? null,
    }
  } catch {
    return { isNexus: false, instanceId: null }
  }
}

// ── Session (read-only, inline) ───────────────────────────────────────────

function getSessionInstanceId(projectPath: string): string | undefined {
  const file = join(homedir(), '.nexus', 'session.json')
  if (!existsSync(file)) return undefined
  try {
    const store = JSON.parse(readFileSync(file, 'utf-8'))
    return store[projectPath]?.instanceId
  } catch {
    return undefined
  }
}

// ── Kill ──────────────────────────────────────────────────────────────────

function isProcessAlive(pid: number): boolean {
  try {
    process.kill(pid, 0)
    return true
  } catch {
    return false
  }
}

async function killByPort(port: string, force = false): Promise<void> {
  let pid: string
  try {
    // -sTCP:LISTEN restricts to the actual listener (excludes our own client
    // socket from the prior health-check fetch — which would otherwise make
    // us SIGTERM ourselves and exit with 143).
    pid = execSync(`lsof -iTCP:${port} -sTCP:LISTEN -t`, { encoding: 'utf-8', stdio: ['pipe', 'pipe', 'pipe'] }).trim()
  } catch {
    consola.warn(`No process found on port ${port}`)
    return
  }

  if (!pid) {
    consola.warn(`No process found on port ${port}`)
    return
  }

  // Filter out our own pid just in case, then take the first listener.
  const pids = pid.split('\n').map(Number).filter(n => n && n !== process.pid)
  if (pids.length === 0) {
    consola.warn(`No process found on port ${port}`)
    return
  }
  const mainPid = pids[0]!

  // Send SIGTERM first
  try {
    process.kill(mainPid, 'SIGTERM')
  } catch {
    consola.warn(`Could not stop process ${mainPid}`)
    return
  }

  // Wait for process to die (up to 5s)
  for (let i = 0; i < 50; i++) {
    if (!isProcessAlive(mainPid)) {
      consola.success(`Backend stopped (pid ${mainPid}, port ${port})`)
      return
    }
    await new Promise(r => setTimeout(r, 100))
  }

  // Process survived SIGTERM — escalate to SIGKILL if --force
  if (force) {
    try {
      process.kill(mainPid, 'SIGKILL')
      consola.success(`Backend killed (pid ${mainPid}, port ${port})`)
    } catch {
      consola.warn(`Could not kill process ${mainPid}`)
    }
    return
  }

  consola.warn(`Process ${mainPid} did not stop after SIGTERM. Use --force to send SIGKILL`)
}

// ── Handler ───────────────────────────────────────────────────────────────

export async function handleStop(options: { force?: boolean } = {}): Promise<void> {
  const port = process.env['PORT'] || '3000'

  // --force: skip all checks, kill by port directly (escalates to SIGKILL)
  if (options.force) {
    await killByPort(port, true)
    return
  }

  // Check 1: Is it a Nexus backend?
  const health = await checkHealth(port)
  if (!health.isNexus) {
    consola.warn(`Process on port ${port} is not a Nexus backend (health check failed)`)
    consola.info('Use --force to stop any process on this port')
    return
  }

  // Check 2: Does instanceId match session?
  const sessionInstanceId = getSessionInstanceId(getProjectPath())
  if (sessionInstanceId && health.instanceId) {
    if (sessionInstanceId !== health.instanceId) {
      consola.warn(
        `Backend on port ${port} is a different instance ` +
        `(session: ${sessionInstanceId.slice(0, 8)}…, running: ${health.instanceId.slice(0, 8)}…)`
      )
      consola.info('Use --force to stop anyway, or run `nexus client login` to update session')
      return
    }
  }

  // All checks passed
  await killByPort(port)
}
